import { Anchor, Box, Card, Divider, Stack, Text, ThemeIcon } from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';

import { MarketingHero } from '../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../components/marketing/MarketingSection';
import { PublicShell } from '../components/marketing/PublicShell';
import { useCmsImage, useCmsSlot } from '../lib/cms';

export const Route = createFileRoute('/community-guidelines')({
  component: CommunityGuidelinesPage,
});

function CommunityGuidelinesPage() {
  const { image: heroImg, ready: heroImgReady } = useCmsImage('community_guidelines.hero_image');
  const heroEyebrow = useCmsSlot<'markdown'>('community_guidelines.hero_eyebrow');
  const heroTitle = useCmsSlot<'markdown'>('community_guidelines.hero_title');
  const heroLede = useCmsSlot<'markdown'>('community_guidelines.hero_lede');
  const lineageQuote = useCmsSlot<'markdown'>('community_guidelines.lineage_quote');
  const lineageBody = useCmsSlot<'markdown'>('community_guidelines.lineage_body');
  const expectations = useCmsSlot<'step_list'>('community_guidelines.expectations');
  const refusals = useCmsSlot<'step_list'>('community_guidelines.refusals');
  const guardrails = useCmsSlot<'step_list'>('community_guidelines.guardrails');
  const lineageEyebrow = useCmsSlot<'markdown'>('community_guidelines.lineage_eyebrow');
  const lineageTitle = useCmsSlot<'markdown'>('community_guidelines.lineage_title');
  const expectationsEyebrow = useCmsSlot<'markdown'>('community_guidelines.expectations_eyebrow');
  const expectationsTitle = useCmsSlot<'markdown'>('community_guidelines.expectations_title');
  const expectationsLede = useCmsSlot<'markdown'>('community_guidelines.expectations_lede');
  const refusalsEyebrow = useCmsSlot<'markdown'>('community_guidelines.refusals_eyebrow');
  const refusalsTitle = useCmsSlot<'markdown'>('community_guidelines.refusals_title');
  const refusalsLede = useCmsSlot<'markdown'>('community_guidelines.refusals_lede');
  const guardrailsEyebrow = useCmsSlot<'markdown'>('community_guidelines.guardrails_eyebrow');
  const guardrailsTitle = useCmsSlot<'markdown'>('community_guidelines.guardrails_title');
  const guardrailsLede = useCmsSlot<'markdown'>('community_guidelines.guardrails_lede');
  const disputeCalloutTitle = useCmsSlot<'markdown'>('community_guidelines.dispute_callout_title');
  const disputeCalloutBody = useCmsSlot<'markdown'>('community_guidelines.dispute_callout_body');
  const disputeCalloutLink = useCmsSlot<'markdown'>('community_guidelines.dispute_callout_link');
  const safetyEyebrow = useCmsSlot<'markdown'>('community_guidelines.safety_eyebrow');
  const safetyTitle = useCmsSlot<'markdown'>('community_guidelines.safety_title');
  const safetyLede = useCmsSlot<'markdown'>('community_guidelines.safety_lede');
  const safetyEmergencyTitle = useCmsSlot<'markdown'>('community_guidelines.safety_emergency_title');
  const safetyEmergencyBodyBefore = useCmsSlot<'markdown'>(
    'community_guidelines.safety_emergency_body_before',
  );
  const safetyEmergencyNumber = useCmsSlot<'markdown'>('community_guidelines.safety_emergency_number');
  const safetyEmergencyBodyAfter = useCmsSlot<'markdown'>(
    'community_guidelines.safety_emergency_body_after',
  );
  const safetyReportTitle = useCmsSlot<'markdown'>('community_guidelines.safety_report_title');
  const safetyReportBodyBefore = useCmsSlot<'markdown'>(
    'community_guidelines.safety_report_body_before',
  );
  const safetyReportEmail = useCmsSlot<'markdown'>('community_guidelines.safety_report_email');
  const safetyReportBodyAfter = useCmsSlot<'markdown'>(
    'community_guidelines.safety_report_body_after',
  );
  const safetyNextTitle = useCmsSlot<'markdown'>('community_guidelines.safety_next_title');
  const safetyNextBody = useCmsSlot<'markdown'>('community_guidelines.safety_next_body');
  const nextStepsTitle = useCmsSlot<'markdown'>('community_guidelines.next_steps_title');

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        lede={heroLede}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.18}
      />

      <MarketingSection
        eyebrow={lineageEyebrow}
        title={lineageTitle}
      >
        <Card
          withBorder
          padding="xl"
          radius="lg"
          style={{
            backgroundColor: 'light-dark(rgba(255, 255, 255, 0.95), var(--mantine-color-dark-6))',
            borderColor: 'var(--mantine-color-ink-1)',
          }}
        >
          <Stack gap="md">
            <Text
              size="xl"
              fw={600}
              style={{ lineHeight: 1.45, color: 'var(--mantine-color-ink-9)' }}
            >
              {lineageQuote}
            </Text>
            <Divider color="ink.1" />
            <Text c="ink.7" size="md" style={{ lineHeight: 1.6 }}>
              {lineageBody}
            </Text>
          </Stack>
        </Card>
      </MarketingSection>

      <MarketingSection
        eyebrow={expectationsEyebrow}
        title={expectationsTitle}
        lede={expectationsLede}
        tinted
      >
        <Stack gap="md">
          {expectations.map((item) => (
            <GuidelineRow key={item.title} title={item.title} body={item.body} tone="affirm" />
          ))}
        </Stack>
      </MarketingSection>

      <MarketingSection
        eyebrow={refusalsEyebrow}
        title={refusalsTitle}
        lede={refusalsLede}
      >
        <Stack gap="md">
          {refusals.map((item) => (
            <GuidelineRow key={item.title} title={item.title} body={item.body} tone="refuse" />
          ))}
        </Stack>
      </MarketingSection>

      <MarketingSection
        eyebrow={guardrailsEyebrow}
        title={guardrailsTitle}
        lede={guardrailsLede}
        tinted
      >
        <Stack gap="md">
          {guardrails.map((item) => (
            <GuidelineRow key={item.title} title={item.title} body={item.body} tone="affirm" />
          ))}
          <Card
            withBorder
            padding="lg"
            radius="md"
            style={{
              backgroundColor: 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))',
              borderColor: 'var(--mantine-color-brand-2)',
            }}
          >
            <Stack gap="xs">
              <Text fw={600} c="ink.9">
                {disputeCalloutTitle}
              </Text>
              <Text c="ink.7" style={{ lineHeight: 1.6 }}>
                {disputeCalloutBody}
              </Text>
              <Box>
                <Anchor component={Link} to="/dispute-resolution" fw={500}>
                  {disputeCalloutLink}
                </Anchor>
              </Box>
            </Stack>
          </Card>
        </Stack>
      </MarketingSection>

      <MarketingSection
        eyebrow={safetyEyebrow}
        title={safetyTitle}
        lede={safetyLede}
      >
        <Stack gap="md">
          <Card
            withBorder
            padding="xl"
            radius="lg"
            style={{
              backgroundColor:
                'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))',
              borderColor: 'var(--mantine-color-brand-3)',
            }}
          >
            <Stack gap="xs">
              <Text fw={700} size="lg" c="ink.9">
                {safetyEmergencyTitle}
              </Text>
              <Text c="ink.7" style={{ lineHeight: 1.65 }}>
                {safetyEmergencyBodyBefore} <strong>{safetyEmergencyNumber}</strong>{' '}
                {safetyEmergencyBodyAfter}
              </Text>
            </Stack>
          </Card>

          <Card
            withBorder
            padding="lg"
            radius="md"
            style={{
              backgroundColor: 'light-dark(rgba(255, 255, 255, 0.9), var(--mantine-color-dark-6))',
              borderColor: 'var(--mantine-color-ink-1)',
            }}
          >
            <Stack gap="xs">
              <Text fw={600} c="ink.9">
                {safetyReportTitle}
              </Text>
              <Text c="ink.7" style={{ lineHeight: 1.65 }}>
                {safetyReportBodyBefore}{' '}
                <Anchor href="mailto:hello@wwaave.com" fw={500}>
                  {safetyReportEmail}
                </Anchor>
                {safetyReportBodyAfter}
              </Text>
            </Stack>
          </Card>

          <Card
            withBorder
            padding="lg"
            radius="md"
            style={{
              backgroundColor: 'light-dark(rgba(255, 255, 255, 0.9), var(--mantine-color-dark-6))',
              borderColor: 'var(--mantine-color-ink-1)',
            }}
          >
            <Stack gap="xs">
              <Text fw={600} c="ink.9">
                {safetyNextTitle}
              </Text>
              <Text c="ink.7" style={{ lineHeight: 1.65 }}>
                {safetyNextBody}
              </Text>
            </Stack>
          </Card>
        </Stack>
      </MarketingSection>

      <MarketingNextSteps
        title={nextStepsTitle}
        destinations={['/our-ethos', '/dispute-resolution', '/our-story']}
      />
    </PublicShell>
  );
}

function GuidelineRow({
  title,
  body,
  tone,
}: {
  title: string;
  body: string;
  tone: 'affirm' | 'refuse';
}) {
  const isAffirm = tone === 'affirm';
  return (
    <Card
      withBorder
      padding="lg"
      radius="md"
      style={{
        backgroundColor: 'light-dark(rgba(255, 255, 255, 0.9), var(--mantine-color-dark-6))',
        borderColor: isAffirm
          ? 'var(--mantine-color-brand-2)'
          : 'var(--mantine-color-ink-2)',
      }}
    >
      <Box style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <ThemeIcon
          size={22}
          radius="xl"
          color={isAffirm ? 'brand.6' : 'ink.3'}
          variant="filled"
          style={{ flexShrink: 0, marginTop: 4 }}
        >
          <Text size="xs" fw={700} c={isAffirm ? 'white' : 'ink.8'}>
            {isAffirm ? '✓' : '×'}
          </Text>
        </ThemeIcon>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} c="ink.9" style={{ lineHeight: 1.4 }}>
            {title}
          </Text>
          <Text c="ink.7" style={{ lineHeight: 1.6 }}>
            {body}
          </Text>
        </Stack>
      </Box>
    </Card>
  );
}
